/**
 * 宗亲面板组件
 * 显示王室族谱，区分本宗和支系
 */

import { useState, useMemo } from 'react'
import { SHANG_FACTIONS } from '../../data/shangDynastyData'
import {
  getFactionRoyalArchive,
  type PresetCharacter
} from '../../data/royalFamilyArchives'
import { useShangGameStore } from '../../stores/shangGameStore'
import { CharacterCard } from '../CharacterCard'
import type { Character, CharacterIdentity } from '../../types/character'
import './ClanPanel.css'

interface ClanPanelProps {
  factionId: string
}

// 族谱节点类型
interface GenealogyNode {
  id: string
  characterId: string
  name: string
  surname: string
  clanName?: string
  title: string
  identity: string
  gender: 'male' | 'female'
  birthYear: number
  deathYear?: number
  isAlive: boolean
  generation: number
  fatherId?: string
  motherId?: string
  spouseIds: string[]
  childrenIds: string[]
  siblingIds: string[]
  isMainBranch: boolean // 是否本宗
  relationToRuler: string // 与君主关系
}

export function ClanPanel({ factionId }: ClanPanelProps) {
  const [activeTab, setActiveTab] = useState<'mainBranch' | 'sideBranches'>('mainBranch')
  const [selectedCharacter, setSelectedCharacter] = useState<GenealogyNode | null>(null)

  // 获取势力信息和当前剧本ID
  const faction = SHANG_FACTIONS.find(f => f.id === factionId)
  const { config } = useShangGameStore()
  const scenarioId = config?.scenarioId

  // 构建族谱数据
  const genealogyData = useMemo(() => {
    if (!faction || !scenarioId) return { mainBranch: [], sideBranches: [], ruler: null as GenealogyNode | null }

    // 从 royalFamilyArchives 获取该势力的王室成员数据（使用正确的scenarioId）
    const archive = getFactionRoyalArchive(scenarioId, factionId)
    if (!archive) return { mainBranch: [], sideBranches: [], ruler: null }

    const nodes: GenealogyNode[] = []
    const ruler = archive.members.find((m: PresetCharacter) => m.identity === 'ruler')
    const rulerId = ruler?.id

    // 创建节点映射
    const nodeMap = new Map<string, GenealogyNode>()

    archive.members.forEach((member: PresetCharacter) => {
      // 确定与君主的关系和是否本宗
      let isMainBranch = false
      let relationToRuler = ''

      if (member.id === rulerId) {
        isMainBranch = true
        relationToRuler = '君主'
      } else if (member.identity === 'heir') {
        isMainBranch = true
        relationToRuler = '世子/太子'
      } else if (member.identity === 'royal') {
        // 根据身份判断
        if (member.title === '王后' || member.title === '夫人') {
          isMainBranch = true
          relationToRuler = '正妻'
        } else if (member.title === '太子') {
          isMainBranch = true
          relationToRuler = '太子'
        } else if (member.title === '公子' || member.title === '公主') {
          isMainBranch = true
          relationToRuler = member.gender === 'male' ? '公子' : '公主'
        } else if (member.title === '公弟') {
          isMainBranch = false
          relationToRuler = '公弟'
        } else {
          isMainBranch = false
          relationToRuler = '宗室'
        }
      } else {
        isMainBranch = false
        relationToRuler = member.title || '宗室'
      }

      const node: GenealogyNode = {
        id: member.id,
        characterId: member.id,
        name: member.name,
        surname: member.surname,
        clanName: member.clanName,
        title: member.title,
        identity: member.identity,
        gender: member.gender,
        birthYear: member.birthYear,
        isAlive: true, // 预设角色默认为在世
        generation: 0, // 稍后计算
        spouseIds: [],
        childrenIds: [],
        siblingIds: [],
        isMainBranch,
        relationToRuler
      }

      nodes.push(node)
      nodeMap.set(node.id, node)
    })

    // 计算世代关系（简化版：基于身份推断）
    nodes.forEach(node => {
      if (node.identity === 'ruler') {
        node.generation = 0
      } else if (node.title === '太子' || node.title === '公子' || node.title === '公主') {
        node.generation = 1
      } else if (node.title === '公弟') {
        node.generation = 0 // 与君主同辈
      } else {
        node.generation = 1
      }
    })

    // 分离本宗和支系
    const mainBranch = nodes.filter(n => n.isMainBranch).sort((a, b) => {
      // 君主第一，然后是王后/夫人，然后是太子，然后是其他子女
      const orderA = getBranchOrder(a)
      const orderB = getBranchOrder(b)
      return orderA - orderB
    })

    const sideBranches = nodes.filter(n => !n.isMainBranch).sort((a, b) => {
      // 按身份排序
      const orderA = getSideBranchOrder(a)
      const orderB = getSideBranchOrder(b)
      return orderA - orderB
    })

    return {
      mainBranch,
      sideBranches,
      ruler: ruler ? nodeMap.get(ruler.id) || null : null
    }
  }, [faction, factionId])



  const { mainBranch, sideBranches, ruler } = genealogyData

  // 渲染族谱节点
  const renderNode = (node: GenealogyNode, index: number, isLast: boolean) => {
    return (
      <div key={node.id} className="genealogy-node-wrapper">
        {/* 连接线 */}
        {index > 0 && (
          <div className="genealogy-connector">
            <div className="connector-line"></div>
          </div>
        )}

        <div
          className={`genealogy-node ${node.isMainBranch ? 'main-branch' : 'side-branch'} ${node.gender} ${node.identity === 'ruler' ? 'ruler' : ''}`}
          onClick={() => setSelectedCharacter(node)}
        >
          <div className="node-avatar">
            <span className="avatar-icon">
              {node.gender === 'male' ? '👤' : '👩'}
            </span>
            {node.identity === 'ruler' && <span className="crown-icon">👑</span>}
          </div>

          <div className="node-info">
            <div className="node-header">
              <span className="node-name">{node.name}</span>
              <span className={`node-title ${node.identity}`}>{node.title}</span>
            </div>

            <div className="node-details">
              <span className="node-relation">{node.relationToRuler}</span>
              <span className="node-age">
                {node.isAlive ? `现年 ${Math.abs(node.birthYear)} 岁` : `卒年 ${Math.abs(node.deathYear || node.birthYear + 50)} 岁`}
              </span>
            </div>

            <div className="node-meta">
              <span className="node-surname">{node.surname}姓{node.clanName ? `${node.clanName}氏` : ''}</span>
              {!node.isAlive && <span className="deceased-tag">已故</span>}
            </div>
          </div>

          <div className="node-actions">
            <button className="action-btn detail-btn" onClick={(e) => {
              e.stopPropagation()
              setSelectedCharacter(node)
            }}>
              详情
            </button>
          </div>
        </div>

        {/* 子节点连接线 */}
        {!isLast && <div className="branch-line"></div>}
      </div>
    )
  }

  return (
    <div className="clan-panel">
      {/* 头部信息 */}
      <div className="clan-header">
        <div className="clan-title-section">
          <h2 className="clan-title">
            {faction?.name}王室族谱
          </h2>
          {ruler && (
            <div className="current-ruler">
              <span className="ruler-label">当前君主：</span>
              <span className="ruler-name">{ruler.name}</span>
              <span className="ruler-title">{ruler.title}</span>
            </div>
          )}
        </div>

        <div className="clan-stats">
          <div className="stat-item">
            <span className="stat-value">{mainBranch.length}</span>
            <span className="stat-label">本宗</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{sideBranches.length}</span>
            <span className="stat-label">支系</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{mainBranch.filter(n => n.gender === 'male').length + sideBranches.filter(n => n.gender === 'male').length}</span>
            <span className="stat-label">男丁</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{mainBranch.filter(n => n.gender === 'female').length + sideBranches.filter(n => n.gender === 'female').length}</span>
            <span className="stat-label">女眷</span>
          </div>
        </div>
      </div>

      {/* 标签页 - 只保留本宗和支系 */}
      <div className="clan-tabs">
        <button
          className={`clan-tab ${activeTab === 'mainBranch' ? 'active' : ''}`}
          onClick={() => setActiveTab('mainBranch')}
        >
          <span className="tab-icon">👑</span>
          本宗
          <span className="tab-count">{mainBranch.length}</span>
        </button>
        <button
          className={`clan-tab ${activeTab === 'sideBranches' ? 'active' : ''}`}
          onClick={() => setActiveTab('sideBranches')}
        >
          <span className="tab-icon">🌿</span>
          支系
          <span className="tab-count">{sideBranches.length}</span>
        </button>
      </div>

      {/* 内容区域 */}
      <div className="clan-content">
        {/* 本宗 */}
        {activeTab === 'mainBranch' && (
          <div className="genealogy-section">
            <div className="section-header">
              <h3>本宗成员</h3>
              <p className="section-desc">君主的直系家族，包括君主本人、王后、太子及子女</p>
            </div>

            {mainBranch.length > 0 ? (
              <div className="genealogy-tree main-branch-tree">
                {mainBranch.map((node, index) => renderNode(node, index, index === mainBranch.length - 1))}
              </div>
            ) : (
              <div className="empty-state">
                <span className="empty-icon">👑</span>
                <p>暂无本宗成员数据</p>
              </div>
            )}
          </div>
        )}

        {/* 支系 */}
        {activeTab === 'sideBranches' && (
          <div className="genealogy-section">
            <div className="section-header">
              <h3>支系成员</h3>
              <p className="section-desc">王室旁支，包括君主的兄弟姐妹及其后裔</p>
            </div>

            {sideBranches.length > 0 ? (
              <div className="genealogy-tree side-branch-tree">
                {sideBranches.map((node, index) => renderNode(node, index, index === sideBranches.length - 1))}
              </div>
            ) : (
              <div className="empty-state">
                <span className="empty-icon">🌿</span>
                <p>暂无支系成员数据</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 角色详情卡片 */}
      {selectedCharacter && (
        <CharacterCard
          character={convertToCharacter(selectedCharacter, factionId)}
          onClose={() => setSelectedCharacter(null)}
        />
      )}
    </div>
  )
}

// 将 GenealogyNode 转换为 Character 类型
function convertToCharacter(node: GenealogyNode, factionId: string): Character {
  const identityMap: Record<string, CharacterIdentity> = {
    ruler: 'royal',
    heir: 'royal',
    royal: 'royal',
    official: 'official',
    general: 'general',
    scholar: 'scholar',
    merchant: 'merchant',
  }

  return {
    id: node.id,
    name: node.name,
    factionId: factionId,
    identity: identityMap[node.identity] || 'royal',
    gender: node.gender,
    birthDate: { year: Math.abs(node.birthYear), month: 1, day: 1 },
    status: node.isAlive ? 'active' : 'deceased',
    abilities: {
      intelligence: 70,
      charisma: 70,
      military: 70,
      administration: 70,
      diplomacy: 70,
      intrigue: 70,
    },
    traits: [],
    loyalty: 80,
    reputation: 60,
    position: node.identity === 'ruler' ? { title: node.title, rank: 1 } : undefined,
    relations: {
      fatherId: node.fatherId,
      motherId: node.motherId,
      spouseId: node.spouseIds[0],
      childrenIds: node.childrenIds,
      siblingIds: node.siblingIds,
      friendIds: [],
      enemyIds: [],
    },
    history: [],
  }
}

// 辅助函数：获取本宗排序权重
function getBranchOrder(node: GenealogyNode): number {
  if (node.identity === 'ruler') return 0
  if (node.title === '王后' || node.title === '夫人') return 1
  if (node.title === '太子') return 2
  if (node.title === '公子') return 3
  if (node.title === '公主') return 4
  return 5
}

// 辅助函数：获取支系排序权重
function getSideBranchOrder(node: GenealogyNode): number {
  if (node.title === '公弟') return 0
  return 1
}
